import type { CreateSearchRequest } from '@legalhermes/shared/api-contracts';
import type { Search, SearchType } from '@legalhermes/shared/domain';
import { useEffect, useState } from 'react';
import { NAME_SEARCH_ENABLED } from '../lib/features';
import { cn } from '../lib/utils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (searchData: CreateSearchRequest) => void;
  editingSearch?: Search | null;
  isSaving?: boolean;
}

interface FormErrors {
  identifier?: string;
  email?: string;
}

const DEFAULT_TYPE: SearchType = NAME_SEARCH_ENABLED ? 'name' : 'process';

export function SearchModal({
  isOpen,
  onClose,
  onSave,
  editingSearch = null,
  isSaving = false,
}: Props) {
  const [type, setType] = useState<SearchType>(DEFAULT_TYPE);
  const [identifier, setIdentifier] = useState('');
  const [email, setEmail] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (!isOpen) return;
    if (editingSearch) {
      setType(editingSearch.type);
      setIdentifier(editingSearch.identifier);
      setEmail(editingSearch.email || '');
      setEnabled(editingSearch.enabled !== false);
    } else {
      setType(DEFAULT_TYPE);
      setIdentifier('');
      setEmail('');
      setEnabled(true);
    }
    setErrors({});
  }, [isOpen, editingSearch]);

  const validate = () => {
    const newErrors: FormErrors = {};

    if (!identifier.trim()) {
      newErrors.identifier =
        type === 'name' ? 'El nombre es requerido' : 'El número de proceso es requerido';
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Email inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (isSaving) return;
    if (!validate()) return;

    onSave({
      type,
      identifier: identifier.trim(),
      email: email.trim() || null,
      enabled,
    });

    onClose();
  };

  if (!isOpen) return null;

  const inputClass =
    'w-full px-4 py-3 border border-border-emphasis rounded-sm text-[0.875rem] pointer-coarse:text-base font-sans bg-surface-inset text-ink placeholder-ink-muted focus:outline-none focus:border-accent focus:shadow-[0_0_0_3px_rgba(200,150,62,0.1)] transition-[border-color,box-shadow] duration-150';

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1000]"
      onClick={onClose}
    >
      <div
        className="bg-surface-raised rounded-lg border border-border-emphasis shadow-[0_8px_32px_rgba(0,0,0,0.5)] max-w-[500px] w-[90%] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-8 py-6 border-b border-border flex justify-between items-center">
          <h3 className="font-serif text-ink">
            {editingSearch ? 'Editar Búsqueda' : 'Agregar Nueva Búsqueda'}
          </h3>
          <button
            className="bg-none border-none text-2xl cursor-pointer text-ink-muted hover:text-ink transition-colors duration-150"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          {/* Selector de tipo — oculto al crear si las búsquedas por nombre están
              desactivadas; se mantiene al editar una búsqueda por nombre existente */}
          {(NAME_SEARCH_ENABLED || editingSearch?.type === 'name') && (
            <div className="mb-6">
              <label className="block mb-2 font-medium text-[0.8125rem] text-ink-secondary tracking-wide">
                Tipo de Búsqueda
              </label>
              <div className="flex flex-col gap-3">
                <label
                  className={cn(
                    'flex items-center gap-3 cursor-pointer select-none text-[0.875rem]',
                    editingSearch && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <input
                    type="radio"
                    value="name"
                    checked={type === 'name'}
                    onChange={(e) => {
                      setType(e.target.value as SearchType);
                      setIdentifier('');
                      setErrors({});
                    }}
                    disabled={!!editingSearch}
                    className="absolute opacity-0 w-0 h-0 pointer-events-none"
                  />
                  <span
                    className={cn(
                      'w-[18px] h-[18px] rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-[border-color,background-color] duration-150',
                      type === 'name'
                        ? 'border-accent bg-accent-subtle'
                        : 'border-border-emphasis bg-transparent'
                    )}
                  >
                    {type === 'name' && <span className="w-2 h-2 rounded-full bg-accent" />}
                  </span>
                  <span className="text-ink-secondary">Por Nombre o Razón Social</span>
                </label>
                <label
                  className={cn(
                    'flex items-center gap-3 cursor-pointer select-none text-[0.875rem]',
                    editingSearch && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <input
                    type="radio"
                    value="process"
                    checked={type === 'process'}
                    onChange={(e) => {
                      setType(e.target.value as SearchType);
                      setIdentifier('');
                      setErrors({});
                    }}
                    disabled={!!editingSearch}
                    className="absolute opacity-0 w-0 h-0 pointer-events-none"
                  />
                  <span
                    className={cn(
                      'w-[18px] h-[18px] rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-[border-color,background-color] duration-150',
                      type === 'process'
                        ? 'border-accent bg-accent-subtle'
                        : 'border-border-emphasis bg-transparent'
                    )}
                  >
                    {type === 'process' && <span className="w-2 h-2 rounded-full bg-accent" />}
                  </span>
                  <span className="text-ink-secondary">Por Número de Proceso</span>
                </label>
              </div>
            </div>
          )}

          {/* Campo de identificador */}
          <div className="mb-6">
            <label className="block mb-2 font-medium text-[0.8125rem] text-ink-secondary tracking-wide">
              {type === 'name' ? 'Nombre o Razón Social' : 'Número de Proceso'}
            </label>
            <input
              type="text"
              className={cn(inputClass, errors.identifier && 'border-destructive')}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={
                type === 'name'
                  ? 'Ej: Juan Pérez, Empresa XYZ S.A.S'
                  : 'Ej: 11001400306120250098100'
              }
            />
            {errors.identifier && (
              <small className="text-destructive text-[0.8125rem] mt-1 block">
                {errors.identifier}
              </small>
            )}
          </div>

          {/* Campo de email */}
          <div className="mb-6">
            <label className="block mb-2 font-medium text-[0.8125rem] text-ink-secondary tracking-wide">
              Email para Notificaciones (Opcional)
            </label>
            <input
              type="email"
              className={cn(inputClass, errors.email && 'border-destructive')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
            />
            {errors.email && (
              <small className="text-destructive text-[0.8125rem] mt-1 block">{errors.email}</small>
            )}
            <small className="text-ink-tertiary text-[0.8125rem] mt-1 block">
              Si no se especifica, se usará el email de administrador
            </small>
          </div>

          {/* Estado de la búsqueda */}
          <div className="mb-6">
            <label className="flex items-start gap-4 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="absolute opacity-0 w-0 h-0 pointer-events-none"
              />
              <span
                className={cn(
                  'relative w-9 h-5 rounded-[10px] border transition-[background-color,border-color] duration-200 flex-shrink-0 mt-px cursor-pointer',
                  "after:content-[''] after:absolute after:top-px after:left-px after:w-4 after:h-4 after:rounded-full after:transition-[transform,background-color] after:duration-200",
                  enabled
                    ? 'bg-accent-subtle border-accent after:bg-accent'
                    : 'bg-white/[0.12] border-border-emphasis after:translate-x-4 after:bg-ink-secondary'
                )}
              />
              <span className="flex flex-col gap-1 min-w-0">
                <span className="text-[0.8125rem] font-medium text-ink-secondary tracking-wide">
                  Búsqueda Activa
                </span>
                <span className="text-[0.8125rem] text-ink-tertiary leading-snug">
                  Solo las búsquedas activas se incluirán en el monitoreo
                </span>
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-6 border-t border-border flex justify-end gap-3">
          <button
            type="button"
            className="px-6 py-3 bg-surface border border-border-emphasis text-ink-secondary rounded-sm text-[0.8125rem] font-medium font-sans inline-flex items-center gap-2 cursor-pointer hover:bg-surface-raised hover:text-ink transition-[background-color,color] duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="px-6 py-3 bg-accent text-ink-inverse border-none rounded-sm text-[0.8125rem] font-medium font-sans inline-flex items-center gap-2 cursor-pointer hover:bg-accent-hover transition-[background-color] duration-150 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-accent"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Guardando…' : editingSearch ? 'Guardar Cambios' : 'Agregar Búsqueda'}
          </button>
        </div>
      </div>
    </div>
  );
}
